'use client';

import {
  FloatingPortal,
  useMergeRefs,
  useTransitionStyles,
} from '@floating-ui/react';
import { isNonNil } from '@vuddy/utils';
import { ReactNode, Ref, useEffect, useState } from 'react';
import { useDropdownContext } from './useDropdownContext';

type Option<T> = T & {
  id: string | number;
  label: ReactNode;
};

interface DropdownContentProps<T> {
  className?: string;
  options: Option<T>[];
  onClick: (id: string | number) => void;
  renderOption?: (option: Option<T>) => ReactNode;
  ref?: Ref<HTMLDivElement>;
  footer?: ReactNode | ((setOpen: (open: boolean) => void) => ReactNode);
  closeOnOptionClick?: boolean;
  optionsWrapperClassName?: string;
  // 부모 요소의 넓이를 사용하여 드롭다운의 넓이를 설정
  hasParentWidth?: boolean;
}

export const DropdownContent = <T,>({
  className,
  options,
  onClick,
  renderOption,
  ref: forwardedRef,
  footer,
  closeOnOptionClick = true,
  optionsWrapperClassName,
  hasParentWidth = false,
  ...props
}: DropdownContentProps<T>) => {
  const { refs, context, floatingStyles, setOpen, getFloatingProps } =
    useDropdownContext();
  const ref = useMergeRefs([refs.setFloating, forwardedRef]);
  const { isMounted, styles: transitionStyles } = useTransitionStyles(context, {
    duration: 300,
  });

  // 트리거 너비를 상태로 관리
  const [triggerWidth, setTriggerWidth] = useState<number | null>(null);

  // 트리거 요소의 너비 변화를 감지
  useEffect(() => {
    if (!hasParentWidth) return;

    const triggerElement = refs?.reference?.current;

    if (!triggerElement) return;

    // 초기 너비 설정
    const updateWidth = () => {
      const width = triggerElement.getBoundingClientRect().width;
      setTriggerWidth(width);
    };

    updateWidth();

    // ResizeObserver로 리사이징 감지
    const resizeObserver = new ResizeObserver(updateWidth);
    resizeObserver.observe(triggerElement as Element);

    return () => {
      resizeObserver.disconnect();
    };
  }, [refs.reference, isMounted, hasParentWidth]);

  return (
    <FloatingPortal>
      {isMounted && (
        <div
          className={className}
          ref={ref}
          style={{
            ...floatingStyles,
            ...transitionStyles,
            ...(hasParentWidth &&
              triggerWidth && {
                width: `${triggerWidth}px`,
              }),
          }}
          {...getFloatingProps(props)}>
          <div className={optionsWrapperClassName}>
            {options.map(option => (
              <div
                key={option.id}
                onClick={e => {
                  onClick(option.id);
                  if (closeOnOptionClick) {
                    setOpen(false);
                  }
                  e.stopPropagation();
                }}>
                {isNonNil(renderOption) ? renderOption(option) : option.label}
              </div>
            ))}
          </div>
          {typeof footer === 'function' ? footer(setOpen) : footer}
        </div>
      )}
    </FloatingPortal>
  );
};
